import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { LOGIN_TYPE } from '@/configs/constants';
import { listDocuments } from '@/lib/firebase/query';
import { homeFirestore } from '@/configs/firebase.config';
import { collections } from '@/utils/constants/fb.collections';

const DEFAULT_COUNTRY = 'DEFAULT';
export const LoginTypeContext = createContext({
    loginType: LOGIN_TYPE.CUSTOM,
    setLoginType: () => {},
    country: DEFAULT_COUNTRY,
    setCountry: () => {},
});

const DEFAULT_CONFIGS = {
    DEFAULT: LOGIN_TYPE.FIREBASE,
};

/**
 * Provider that derives loginType from country and supports manual override.
 * Optimized with memoization and conditional updates to avoid extra renders.
 */
export function LoginTypeProvider({ children }) {
    const [configs, setConfigs] = useState(DEFAULT_CONFIGS);
    const [country, setCountry] = useState(DEFAULT_COUNTRY);
    const deriveType = useCallback(
        (country) => {
            if (!country) return DEFAULT_CONFIGS[DEFAULT_COUNTRY];
            const _type = configs[country] || configs[DEFAULT_COUNTRY];
            return _type;
        },
        [configs]
    );

    const [type, setType] = useState(() => deriveType(DEFAULT_COUNTRY));

    useEffect(() => {
        async function loadCountry() {
            try {
                const config = await listDocuments(collections.otpConfig, {}, homeFirestore); // global
                let _config = Object.fromEntries(config.map(({ id, ...rest }) => [id, rest.login]));
                setConfigs(_config);
            } catch (err) {
                console.error('Failed to load country config:', err);
            }
        }
        loadCountry();
    }, []);

    useEffect(() => {
        const newType = deriveType(country);
        if (newType !== type) {
            setType(newType);
        }
    }, [country, deriveType, configs]);

    const setLoginType = useCallback((newType) => {
        setType(newType);
    }, []);

    const contextValue = useMemo(
        () => ({
            loginType: type,
            isCustom: type === LOGIN_TYPE.CUSTOM,
            isFirebase: type === LOGIN_TYPE.FIREBASE,
            setLoginType,
            country,
            setCountry,
        }),
        [type, setLoginType, country, setCountry]
    );

    return <LoginTypeContext.Provider value={contextValue}>{children}</LoginTypeContext.Provider>;
}
